// Morningstar Document Library constants
// Used for document type configuration, display, and language-aware search

import type { LocaleCode } from "./i18n";

export const MS_DOCUMENT_TYPES = [
  { id: "1", name: "Prospectus" },
  { id: "4", name: "Annual Report" },
  { id: "5", name: "Semi-Annual Report" },
  { id: "51", name: "Rulebook" },
  { id: "52", name: "Factsheet" },
  { id: "65", name: "Information Folder" },
  { id: "68", name: "Application Form" },
  { id: "74", name: "KIID (Key Investor Information Document)" },
  { id: "109", name: "Information Memorandum" },
  { id: "125", name: "Factsheet Institutional" },
  { id: "127", name: "Shareholder Notice" },
  { id: "299", name: "PRIIP KID (Key Information Document)" },
  { id: "343", name: "Manager Commentary Institutional" },
  { id: "399", name: "Periodic SFDR Annex" },
] as const;

export const MS_LANGUAGES = [
  { id: "444", name: "Bulgarian" },
  { id: "449", name: "Danish" },
  { id: "450", name: "Dutch/Flemish" },
  { id: "451", name: "English" },
  { id: "453", name: "Finnish" },
  { id: "454", name: "French" },
  { id: "455", name: "German" },
  { id: "456", name: "Greek" },
  { id: "460", name: "Icelandic" },
  { id: "461", name: "Italian" },
  { id: "464", name: "Latvian" },
  { id: "465", name: "Lithuanian" },
  { id: "467", name: "Norwegian" },
  { id: "468", name: "Polish" },
  { id: "469", name: "Portuguese" },
  { id: "471", name: "Spanish" },
  { id: "472", name: "Swedish" },
] as const;

export const MS_MARKETS = [
  { id: "1427", name: "Austria" },
  { id: "1428", name: "Belgium" },
  { id: "1429", name: "Bulgaria" },
  { id: "1432", name: "Switzerland" },
  { id: "1436", name: "Cyprus" },
  { id: "1437", name: "Czech Republic" },
  { id: "1438", name: "Germany" },
  { id: "1439", name: "Denmark" },
  { id: "1440", name: "Spain" },
  { id: "1442", name: "Finland" },
  { id: "1443", name: "France" },
  { id: "1444", name: "United Kingdom" },
  { id: "1446", name: "Greece" },
  { id: "1447", name: "Hong Kong" },
  { id: "1448", name: "Hungary" },
  { id: "1452", name: "Ireland" },
  { id: "1453", name: "Iceland" },
  { id: "1455", name: "Italy" },
  { id: "1459", name: "Liechtenstein" },
  { id: "1460", name: "Lithuania" },
  { id: "1461", name: "Luxembourg" },
  { id: "1462", name: "Latvia" },
  { id: "1464", name: "Malta" },
  { id: "1466", name: "Netherlands" },
  { id: "1467", name: "Norway" },
  { id: "1470", name: "Poland" },
  { id: "1471", name: "Portugal" },
  { id: "1472", name: "Romania" },
  { id: "1474", name: "Singapore" },
  { id: "1477", name: "Sweden" },
  { id: "1526", name: "Gibraltar" },
  { id: "1548", name: "United Arab Emirates" },
] as const;

// Lookup maps for quick access by ID
export const MS_DOCUMENT_TYPES_MAP = Object.fromEntries(
  MS_DOCUMENT_TYPES.map((t) => [t.id, t.name]),
) as Record<string, string>;

export const MS_LANGUAGES_MAP = Object.fromEntries(
  MS_LANGUAGES.map((l) => [l.id, l.name]),
) as Record<string, string>;

export const MS_MARKETS_MAP = Object.fromEntries(MS_MARKETS.map((m) => [m.id, m.name])) as Record<
  string,
  string
>;

// Partial mapping between application locales and Morningstar language IDs.
// Not every app locale has a Morningstar counterpart (ar, ja_JP, hi_IN, zh_CN,
// zh_TW do not), and not every Morningstar language has an app locale — callers
// must treat "no mapping" (undefined) as a normal outcome, not an error.
export const LOCALE_TO_MS_LANGUAGE_ID: Partial<Record<LocaleCode, string>> = {
  en_GB: "451",
  fr_FR: "454",
  es_ES: "471",
  pt_BR: "469",
  de_DE: "455",
  it_IT: "461",
  nl_NL: "450",
  nb_NO: "467",
  sv_SE: "472",
  fi_FI: "453",
  da_DK: "449",
  is_IS: "460",
};

const MS_LANGUAGE_ID_TO_LOCALE: Record<string, LocaleCode> = Object.fromEntries(
  Object.entries(LOCALE_TO_MS_LANGUAGE_ID).map(([locale, msId]) => [msId, locale as LocaleCode]),
);

export function localeToMsLanguageId(locale: LocaleCode): string | undefined {
  return LOCALE_TO_MS_LANGUAGE_ID[locale];
}

export function msLanguageIdToLocale(msLanguageId: string): LocaleCode | undefined {
  return MS_LANGUAGE_ID_TO_LOCALE[msLanguageId];
}

// The QL-44 backfill rule: a document's stored Morningstar language ID maps to
// its locale only when the owning tenant supports that locale; everything else
// (unmapped MS ID, unsupported locale, uploads with no MS language) falls back
// to the tenant's default language. The 0108 backfill migration encodes this
// same rule in SQL — keep the two in sync.
export function resolveBackfillLocale(
  msLanguageId: string | null,
  supportedLanguages: string[],
  defaultLanguage: string,
): string {
  if (msLanguageId) {
    const locale = msLanguageIdToLocale(msLanguageId);
    if (locale && supportedLanguages.includes(locale)) return locale;
  }
  return defaultLanguage;
}
